import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Users,
  ShoppingBag,
  IndianRupee,
  Train,
  TrendingUp,
  Activity,
  WifiOff,
  RefreshCw,
} from 'lucide-react';
import { API } from '../../config';

// Shimmer Skeleton for Admin Dashboard with visible moving light wave
const DashboardSkeleton = () => (
  <div className="space-y-6">
    {/* Title Skeleton */}
    <div className="space-y-2">
      <div className="h-8 w-48 shimmer-wave rounded-lg" />
      <div className="h-4 w-64 shimmer-wave rounded" />
    </div>

    {/* 4 Stat Cards Skeleton */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm space-y-3">
          <div className="w-11 h-11 rounded-xl shimmer-wave" />
          <div className="h-3 w-20 shimmer-wave rounded" />
          <div className="h-7 w-28 shimmer-wave rounded-lg" />
        </div>
      ))}
    </div>

    {/* 2 Middle Analytics Cards Skeleton */}
    <div className="grid md:grid-cols-2 gap-4">
      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
        <div className="h-5 w-36 shimmer-wave rounded" />
        <div className="flex items-center gap-5">
          <div className="w-24 h-24 rounded-full shimmer-wave shrink-0" />
          <div className="space-y-2.5 flex-1">
            <div className="h-3.5 w-3/4 shimmer-wave rounded" />
            <div className="h-3.5 w-5/6 shimmer-wave rounded" />
            <div className="h-3.5 w-1/2 shimmer-wave rounded" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
        <div className="h-5 w-32 shimmer-wave rounded" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex justify-between items-center py-1">
              <div className="h-4 w-28 shimmer-wave rounded" />
              <div className="h-4 w-16 shimmer-wave rounded" />
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Orders Table Skeleton */}
    <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
      <div className="h-5 w-36 shimmer-wave rounded" />
      <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-10 w-full shimmer-wave rounded-xl" />
        ))}
      </div>
    </div>
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(false);
    const t = localStorage.getItem('tp_admin_token');
    const start = Date.now();
    try {
      const r = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${t}` },
        timeout: 10000,
      });
      // Ensure smooth skeleton visibility for at least 350ms so user sees the shimmer
      const elapsed = Date.now() - start;
      if (elapsed < 350) {
        await new Promise((res) => setTimeout(res, 350 - elapsed));
      }
      setStats(r.data);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (error && !stats) {
    return (
      <div className="space-y-6">
        {/* Offline / Error Fallback Notice */}
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
              <WifiOff size={20} />
            </div>
            <div>
              <h4 className="text-sm font-bold text-rose-900">Network / Server Connection Failed</h4>
              <p className="text-xs text-rose-600 mt-0.5">Could not fetch the latest dashboard metrics. Please check your network.</p>
            </div>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-rose-600 text-white hover:bg-rose-700 transition-colors cursor-pointer shadow-sm shadow-rose-600/20"
          >
            <RefreshCw size={14} /> Retry Now
          </button>
        </div>

        {/* Shimmer skeleton display below error */}
        <DashboardSkeleton />
      </div>
    );
  }

  const s = stats || {
    totalUsers: 0,
    totalOrders: 0,
    revenue: 0,
    activeTrains: 0,
    scrapeSuccess: 0,
    topRoutes: [],
    recentOrders: [],
  };

  const cards = [
    { title: 'Total Users', value: s.totalUsers.toLocaleString(), icon: Users, color: 'from-blue-500 to-indigo-600' },
    { title: 'Total Orders', value: s.totalOrders.toLocaleString(), icon: ShoppingBag, color: 'from-orange-500 to-amber-600' },
    { title: 'Revenue', value: '₹' + s.revenue.toLocaleString(), icon: IndianRupee, color: 'from-emerald-500 to-teal-600' },
    { title: 'Trains Tracked', value: s.activeTrains, icon: Train, color: 'from-violet-600 to-indigo-700' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Overview of TrainPanda operations & live telemetry</p>
        </div>
        <button
          onClick={load}
          title="Refresh Data"
          className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 shadow-sm transition-all cursor-pointer"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* 4 Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.title} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
            <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${c.color} text-white flex items-center justify-center shadow-md`}>
              <c.icon size={20} />
            </div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mt-4">{c.title}</div>
            <div className="text-2xl sm:text-3xl font-black text-slate-900 mt-1">{c.value}</div>
          </div>
        ))}
      </div>

      {/* Analytics Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-extrabold text-slate-900 flex items-center gap-2 mb-4 text-base">
            <Activity size={18} className="text-orange-500" /> Scraping Health
          </h3>
          <div className="flex items-center gap-6">
            <div className="relative w-24 h-24 shrink-0">
              <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                <path d="M18 2 a 16 16 0 0 1 0 32 a 16 16 0 0 1 0 -32" fill="none" stroke="#F1F5F9" strokeWidth="3.5" />
                <path
                  d="M18 2 a 16 16 0 0 1 0 32 a 16 16 0 0 1 0 -32"
                  fill="none"
                  stroke="#F97316"
                  strokeWidth="3.5"
                  strokeDasharray={`${s.scrapeSuccess}, 100`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center font-black text-slate-900 text-lg">
                {s.scrapeSuccess}%
              </div>
            </div>
            <div className="text-xs sm:text-sm text-slate-600 space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span>Success rate last 24h</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500" />
                <span>Multi-source aggregation with fallback</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-violet-500" />
                <span>Cache hit 78%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-extrabold text-slate-900 flex items-center gap-2 mb-4 text-base">
            <TrendingUp size={18} className="text-orange-500" /> Top Routes
          </h3>
          <div className="space-y-2">
            {(s.topRoutes || []).map((r) => (
              <div key={r.route} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-sm">
                <span className="text-slate-800 font-bold">{r.route}</span>
                <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">
                  {r.count} searches
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      {(s.recentOrders || []).length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-extrabold text-slate-900 mb-4 text-base">Recent Orders</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 pb-2">
                  <th className="py-2.5">Order</th>
                  <th>Restaurant</th>
                  <th>Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {s.recentOrders.slice(0, 5).map((o) => (
                  <tr key={o.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="py-3 font-mono font-bold text-xs text-slate-700">{o.id.slice(0, 8).toUpperCase()}</td>
                    <td className="font-medium text-slate-800">{o.restaurant_name}</td>
                    <td className="font-black text-slate-900">₹{o.total}</td>
                    <td>
                      <span className="text-xs font-bold bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full">
                        {o.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
